import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { Database, Search, Loader2, Pencil, ShieldAlert, Activity, Users, Flag, FileUp, PawPrint, EyeOff, Trees } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import AdminLocationEditModal from "../components/AdminLocationEditModal";

function isAdmin(user) { return !!(user && user.is_admin); }

const STATUS_FILTERS = [
  { v: "all", label: "All" },
  { v: "approved", label: "Approved" },
  { v: "pending", label: "Pending" },
  { v: "rejected", label: "Rejected" },
];
const TYPE_FILTERS = [
  { v: "all", label: "Any" },
  { v: "hiking", label: "Hiking" },
  { v: "camping", label: "Camping" },
  { v: "basecamp", label: "Basecamp" },
];

function StatusPill({ status }) {
  const map = {
    approved: "bg-brand-primary/15 text-brand-primary border-brand-primary/30",
    pending: "bg-[#D19C4C]/15 text-[#8E5B16] border-[#D19C4C]/40",
    rejected: "bg-brand-error/15 text-brand-error border-brand-error/30",
  };
  const cls = map[status] || "bg-brand-sand text-brand-subtle border-brand-border";
  return (
    <span data-testid={`status-pill-${status}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${cls}`}>
      {status}
    </span>
  );
}

export default function AdminLocations() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [skip, setSkip] = useState(0);
  const [editing, setEditing] = useState(null);
  const LIMIT = 50;

  // Skip the locations query when the user has narrowed to "basecamp" only —
  // saves a round-trip and the empty result state.
  const list = useQuery({
    queryKey: ["admin-locations", { q, status, type, skip }],
    enabled: !!user && isAdmin(user) && type !== "basecamp",
    queryFn: async () => {
      const params = { limit: LIMIT, skip };
      if (q.trim()) params.q = q.trim();
      if (status !== "all") params.status = status;
      if (type !== "all") params.type = type;
      return (await api.get("/admin/locations", { params })).data;
    },
    keepPreviousData: true,
  });

  // Basecamps are a small curated set — fetch them all once and merge/filter
  // client-side. We skip the fetch when the operator has narrowed the type
  // filter away from basecamps.
  const basecamps = useQuery({
    queryKey: ["admin-basecamps-all"],
    enabled: !!user && isAdmin(user) && (type === "all" || type === "basecamp"),
    queryFn: async () => (await api.get("/basecamps", { params: { limit: 500 } })).data.items || [],
    keepPreviousData: true,
  });

  if (loading) return <div className="p-8 text-brand-subtle">Loading…</div>;
  if (!user || !isAdmin(user)) return <Navigate to="/" replace />;

  // Shape each basecamp into the same row-shape the table expects. `source`
  // stays "basecamp" so operators can tell them apart in the ID sub-row, and
  // `_kind` is a client-only flag we use to switch the Edit button target.
  const shapeBasecamp = (b) => ({
    location_id: b.basecamp_id,
    name: b.name,
    region: "Basecamp",
    type: "basecamp",
    status: "approved",
    source: "basecamp",
    ozzy_verified: false,
    editor_pick: false,
    hidden: false,
    _kind: "basecamp",
  });

  const bcRowsAll = (basecamps.data || []).map(shapeBasecamp);
  // Client-side text + status filter — matches the server-side behaviour on
  // locations so both feeds honour the same search box.
  const bcRowsFiltered = bcRowsAll.filter((r) => {
    if (q.trim() && !r.name.toLowerCase().includes(q.trim().toLowerCase())) return false;
    if (status !== "all" && r.status !== status) return false;
    return true;
  });

  // Assemble the visible items list depending on which lens the operator has
  // chosen. When "all" is active, basecamps ride along on the first page and
  // pagination continues to be driven by the (much larger) locations feed.
  let items = [];
  let total = 0;
  if (type === "basecamp") {
    total = bcRowsFiltered.length;
    items = bcRowsFiltered.slice(skip, skip + LIMIT);
  } else if (type === "all") {
    const locItems = list.data?.items || [];
    // Only surface basecamps on page 1 so their row order is stable and we
    // don't spam them onto every page of the paginated locations feed.
    const bcSlice = skip === 0 ? bcRowsFiltered.slice(0, Math.min(bcRowsFiltered.length, LIMIT)) : [];
    items = [...bcSlice, ...locItems];
    total = (list.data?.total ?? 0) + bcRowsFiltered.length;
  } else {
    items = list.data?.items || [];
    total = list.data?.total ?? 0;
  }

  const page = Math.floor(skip / LIMIT) + 1;
  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const isFetching = list.isFetching || basecamps.isFetching;
  const isLoading = (type === "basecamp" ? basecamps.isLoading : list.isLoading);

  const reset = () => setSkip(0);

  const editRow = (row) => {
    // Basecamps get their own dedicated edit page (distinct schema); trails
    // and campsites still use the in-page modal.
    if (row._kind === "basecamp") {
      navigate(`/admin/basecamps/${row.location_id}`);
      return;
    }
    setEditing(row);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-locations-page">
      <header className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="td-section-label">Admin</div>
          <h1 className="font-heading text-4xl font-semibold text-brand-text mt-1 flex items-center gap-3">
            <Database size={28} className="text-brand-primary" /> All locations
          </h1>
          <p className="text-brand-subtle text-sm mt-1">Search, filter, edit anything in the trails &amp; campsites database.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/moderation" data-testid="locations-to-moderation" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <ShieldAlert size={14} /> Pending
          </Link>
          <Link to="/admin/trails/new" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <FileUp size={14} /> Add trail
          </Link>
          <Link to="/admin/basecamps/new" data-testid="admin-add-basecamp-link" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Trees size={14} /> Add basecamp
          </Link>
          <Link to="/admin/telemetry" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Activity size={14} /> Telemetry
          </Link>
          <Link to="/admin/users" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Users size={14} /> Users
          </Link>
          <Link to="/admin/reports" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Flag size={14} /> Reports
          </Link>
        </div>
      </header>

      {/* Filter bar */}
      <div className="td-card p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-subtle" />
            <input
              data-testid="admin-loc-search"
              value={q}
              onChange={(e) => { setQ(e.target.value); reset(); }}
              placeholder="Search by name…"
              className="td-input pl-9"
            />
          </div>
          <select
            data-testid="admin-loc-status-filter"
            value={status}
            onChange={(e) => { setStatus(e.target.value); reset(); }}
            className="td-input !w-auto"
          >
            {STATUS_FILTERS.map((s) => <option key={s.v} value={s.v}>Status: {s.label}</option>)}
          </select>
          <select
            data-testid="admin-loc-type-filter"
            value={type}
            onChange={(e) => { setType(e.target.value); reset(); }}
            className="td-input !w-auto"
          >
            {TYPE_FILTERS.map((s) => <option key={s.v} value={s.v}>Type: {s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="td-card overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between border-b border-brand-border/60">
          <div className="td-section-label">{total.toLocaleString("en-GB")} matching</div>
          {isFetching && <Loader2 size={14} className="animate-spin text-brand-subtle" />}
        </div>

        {items.length === 0 && !isLoading ? (
          <div className="py-12 text-center text-brand-subtle" data-testid="admin-loc-empty">No locations match those filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-sand/40 text-brand-subtle text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2 hidden sm:table-cell">Region</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Type</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2 hidden lg:table-cell">Flags</th>
                  <th className="text-right px-4 py-2">Edit</th>
                </tr>
              </thead>
              <tbody>
                {items.map((loc) => (
                  <tr key={`${loc._kind || "loc"}-${loc.location_id}`} className="border-t border-brand-border/40" data-testid={`loc-row-${loc.location_id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-brand-text truncate max-w-[280px] flex items-center gap-1.5" title={loc.name}>
                        {loc._kind === "basecamp" && <Trees size={12} className="text-brand-accent shrink-0" />}
                        <span className="truncate">{loc.name}</span>
                      </div>
                      <div className="text-[11px] text-brand-subtle truncate max-w-[280px]">{loc.location_id} · {loc.source}</div>
                    </td>
                    <td className="px-4 py-2.5 hidden sm:table-cell text-brand-subtle">{loc.region || "—"}</td>
                    <td className="px-4 py-2.5 hidden md:table-cell text-brand-subtle capitalize">{loc.type}</td>
                    <td className="px-4 py-2.5"><StatusPill status={loc.status} /></td>
                    <td className="px-4 py-2.5 hidden lg:table-cell">
                      <div className="flex items-center gap-1 flex-wrap">
                        {loc.ozzy_verified && (
                          <span title="Ozzy Verified" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-accent/15 text-brand-accent border border-brand-accent/30">
                            <PawPrint size={10} /> Ozzy
                          </span>
                        )}
                        {loc.editor_pick && <span className="text-[10px] text-brand-accent">★</span>}
                        {loc.hidden && (
                          <span title="Hidden from public" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-sand text-brand-subtle border border-brand-border">
                            <EyeOff size={10} /> hidden
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        data-testid={`loc-edit-btn-${loc.location_id}`}
                        onClick={() => editRow(loc)}
                        className="td-btn-secondary !py-1.5 !px-3 !text-xs whitespace-nowrap"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-brand-border/60 flex items-center justify-between text-xs text-brand-subtle">
          <span>Page {page} of {pages.toLocaleString("en-GB")}</span>
          <div className="flex gap-2">
            <button
              data-testid="admin-loc-prev"
              onClick={() => setSkip(Math.max(0, skip - LIMIT))}
              disabled={skip === 0}
              className="td-btn-secondary !py-1.5 !px-3 !text-xs disabled:opacity-40"
            >
              Prev
            </button>
            <button
              data-testid="admin-loc-next"
              onClick={() => setSkip(skip + LIMIT)}
              disabled={page >= pages}
              className="td-btn-secondary !py-1.5 !px-3 !text-xs disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {editing && <AdminLocationEditModal location={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
